/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { InfoCircleOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'
import type { TooltipProps } from 'antd'
import React from 'react'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  font,
  paragraphSmall,
  paragraphXSmall,
  weightSemiBold,
} from 'src/components/css'

export interface RichTooltipProps {
  /** Heading — the thing being explained. Rendered in Title Case. */
  title: string
  /**
   * What it MEANS, in a sentence or two. A node is accepted for the rare
   * explanation that is a LIST rather than a sentence (a column legend, say) —
   * pass `bodyText` alongside it so assistive tech still gets the words.
   */
  body: React.ReactNode
  /**
   * Plain-text stand-in for a non-string `body`, used to build the anchor's
   * accessible name. Ignored when `body` is already a string.
   */
  bodyText?: string
  /**
   * What is true of THIS one, read before the general meaning — an invited row's
   * "Sent Oct 2, 2026 to sam@x.io" (Jeff, Oct 2: say what the state means AND
   * this instance's own facts). Shown in a box tinted with `color`, a 6-digit hex
   * token, with an optional icon in the same colour. `text` is the plain text of
   * `content`, for the anchor's accessible name.
   */
  fact?: {
    content: React.ReactNode
    text: string
    color: string
    icon?: React.ReactNode
  }
  /**
   * What happens NEXT — the state this leads to, or the action that moves it on.
   * This is the half that stops a user having to open another screen to find
   * out, so prefer supplying it.
   */
  next?: string
  placement?: TooltipProps['placement']
  /**
   * antd caps `.ant-tooltip-inner` at 250px, which wraps rich copy into a narrow
   * ribbon. 300 reads as a paragraph without becoming a wall.
   */
  maxWidth?: number
  /**
   * Set false when the child is ALREADY focusable (a link, a button). The
   * wrapper otherwise adds a SECOND tab stop for the same thing, and its
   * aria-label would compete with the child's own name. Focus still bubbles from
   * the child, so the tooltip opens either way.
   */
  focusable?: boolean
  /**
   * Stacking context to sit above. antd's default (1070) renders BEHIND the
   * app's modal wrappers (the profile modal is 10001), so anything used inside a
   * modal must raise this or the explanation opens invisibly underneath it.
   */
  zIndex?: number
  /**
   * Let the anchor shrink below its content as a flex item, so a label inside
   * that truncates (a name in a narrow column) keeps its ellipsis instead of
   * overflowing.
   */
  shrink?: boolean
  children: React.ReactElement
}

/** Breathing room around the rich copy — antd's default is tight for a paragraph. */
const OVERLAY_PADDING = '10px 12px'

/**
 * Stitch the parts into one spoken sentence without doubling terminal
 * punctuation — copy comes from i18n, where some strings end in a period and
 * some do not, and "this spot.. Assign a player" is what naive joining reads as.
 */
const joinSentences = (parts: (string | undefined)[]): string =>
  parts
    .filter((part): part is string => Boolean(part))
    .reduce(
      (acc, part) =>
        acc ? `${acc}${/[.!?]$/.test(acc) ? '' : '.'} ${part}` : part,
      ''
    )

/**
 * THE tooltip pattern for explanatory copy.
 *
 * The product had three treatments in play — a bespoke `StatisticsTable/Tooltip`,
 * bare antd `Tooltip` one-liners and antd `Popover` — so the same kind of
 * explanation looked like three different things. This is the one to reach for
 * whenever the answer to "what does this mean?" is longer than a label:
 * title + meaning + what-happens-next, one treatment everywhere.
 *
 * Bare `<Tooltip title='one short hint' />` is still fine for a literal one-line
 * hint (a disabled reason, a truncated value) — it renders in the same visual
 * family. The rule is about EXPLANATIONS, not about banning antd's primitive.
 *
 * The anchor is deliberately focusable with the full text as its accessible
 * name: an explanation nobody can reach without a mouse is not an explanation.
 */
const RichTooltip: React.FC<RichTooltipProps> = ({
  title,
  body,
  bodyText,
  fact,
  next,
  placement = 'top',
  maxWidth = 300,
  focusable = true,
  zIndex,
  shrink = false,
  children,
}) => {
  const heading = capitalizeWords(title)
  // Screen readers get the whole thing off the anchor, so the explanation does
  // not depend on the overlay opening at all. A node body cannot be spoken, so
  // `bodyText` is what carries it — without one the anchor announces the
  // heading alone rather than a stringified React element.
  const accessibleText = joinSentences([
    heading,
    fact?.text,
    typeof body === 'string' ? body : bodyText,
    next,
  ])

  // Built here rather than inline in the JSX: these are antd overlay props, not
  // React style props, but `react-native/no-inline-styles` cannot tell the
  // difference and flags any literal-valued object in a *Style prop.
  const overlayBoxStyle = { maxWidth }
  const overlayTextStyle = { maxWidth, padding: OVERLAY_PADDING }

  const overlay = (
    <div css={contentStyle}>
      <span css={titleStyle}>{heading}</span>
      {fact ? (
        <div css={factStyle(fact.color)} data-testid='rich-tooltip-fact'>
          {fact.icon ? (
            <span aria-hidden='true' css={factIconStyle(fact.color)}>
              {fact.icon}
            </span>
          ) : null}
          <span>{fact.content}</span>
        </div>
      ) : null}
      {/* A div, not a p: a node body may itself be block content, and a <p>
          cannot legally contain it (React logs, the browser un-nests it). */}
      <div css={bodyStyle}>{body}</div>
      {next ? (
        <p css={nextStyle} data-testid='rich-tooltip-next'>
          <span aria-hidden='true' css={arrowStyle}>
            →
          </span>
          {next}
        </p>
      ) : null}
    </div>
  )

  return (
    <Tooltip
      title={overlay}
      placement={placement}
      // antd triggers on hover ALONE by default, which puts the explanation out
      // of reach of a keyboard — the focusable anchor below is pointless without
      // this.
      trigger={['hover', 'focus']}
      zIndex={zIndex}
      overlayStyle={overlayBoxStyle}
      overlayInnerStyle={overlayTextStyle}
    >
      <span
        css={[anchorStyle, shrink && shrinkStyle]}
        tabIndex={focusable ? 0 : undefined}
        aria-label={focusable ? accessibleText : undefined}
        data-testid='rich-tooltip-anchor'
      >
        {children}
      </span>
    </Tooltip>
  )
}

export default RichTooltip

export type InfoTooltipProps = Omit<RichTooltipProps, 'children'> & {
  /** Icon size in px — match the text it sits beside. */
  size?: number
}

/**
 * The info-icon affordance for the same pattern.
 *
 * A tooltip with no visible affordance is a tooltip nobody hovers, which is how
 * explanations end up written and never read. Use this wherever a label needs a
 * "what is this?" alongside it.
 */
export const InfoTooltip: React.FC<InfoTooltipProps> = ({
  size = 13,
  ...tooltipProps
}) => (
  <RichTooltip {...tooltipProps}>
    <InfoCircleOutlined css={iconStyle} style={{ fontSize: size }} />
  </RichTooltip>
)

const anchorStyle = css`
  display: inline-flex;
  align-items: center;
  cursor: help;

  /* The focus ring is the only thing telling a keyboard user this holds an
     explanation — do not remove it without replacing the cue. */
  &:focus-visible {
    outline: 2px solid ${colors.INFO_200};
    outline-offset: 2px;
    border-radius: 4px;
  }
`
/** A flex item may shrink below its content: the label inside truncates. */
const shrinkStyle = css`
  min-width: 0;
  max-width: 100%;
`
/**
 * The hover target IS the icon — it is the only visible thing inside the anchor,
 * so `&:hover` here is equivalent to hovering the anchor.
 *
 * Do NOT rewrite this as `${anchorStyle}:hover &`. Interpolating a `css` result
 * into SELECTOR position does not reference the parent's class: emotion merges
 * the parent's declarations into THIS class and emits a self-referential
 * `.x:hover .x`, which can never match. Verified against the emitted stylesheet —
 * the icon silently inherited the anchor's `display`/`cursor` and never changed
 * colour on hover.
 *
 * Keyboard users get the anchor's focus ring instead; focus lands on the anchor,
 * never on this icon.
 */
const iconStyle = css`
  color: ${colors.NEUTRAL_70};
  transition: color 0.15s ease;

  &:hover {
    color: ${colors.NEUTRAL_0};
  }
`
const contentStyle = css`
  ${font.body};
  display: flex;
  flex-direction: column;
  gap: 6px;
`
const titleStyle = css`
  ${paragraphSmall};
  ${weightSemiBold};
  color: ${colors.NEUTRAL_0};
`
/**
 * This instance's facts: a box tinted with its colour (the house `${hex}29` alpha
 * suffix, ~16%), so the specific reads before the general explanation under it.
 */
const factStyle = (color: string) => css`
  ${paragraphSmall};
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  background: ${color}29;
  color: ${colors.NEUTRAL_0};
  line-height: 1.45;
  /* An email address has no spaces to wrap at, and a long one would run out of
     the 300px tooltip. */
  overflow-wrap: anywhere;
`
/** Level with the first line of the fact. */
const factIconStyle = (color: string) => css`
  display: inline-flex;
  flex-shrink: 0;
  margin-top: 3px;
  color: ${color};
`
const bodyStyle = css`
  ${paragraphXSmall};
  color: ${colors.NEUTRAL_30};
  margin: 0;
  line-height: 1.5;
`
const nextStyle = css`
  ${paragraphXSmall};
  display: flex;
  gap: 6px;
  color: ${colors.NEUTRAL_70};
  margin: 0;
  line-height: 1.5;
`
const arrowStyle = css`
  flex-shrink: 0;
`
